// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { IdentifierString } from "@wallet-standard/base";
import type { FrostAppState } from "../types";

/**
 * Storage version - increment when schema changes.
 */
const STORAGE_VERSION = 1;

interface StorageSchema {
  version: number;
  state: SerializedState;
}

/**
 * Serialized state for persistence.
 *
 * Design decisions:
 * - status is NOT persisted: Always loads as "disconnected", reconnect() handles re-establishing
 * - chainId is NOT persisted: Derived from client.getChainIdentifier() at runtime
 * - wallet icons are NOT persisted: Reduces storage size, loaded fresh from wallet-standard
 */
interface SerializedState {
  walletName: string | null;
  accountAddress: string | null;
  connectedAt: number | null;
  sessionExpiresAt: number | null;
  lastUsedAt: number;
  userDisconnected: boolean;
  wallets: [string, SerializedWallet][];
  accounts: [string, SerializedAccount][];
}

interface SerializedWallet {
  name: string;
  // icon intentionally excluded - loaded fresh from wallet-standard on discovery
  chains: string[];
  features: string[];
  installedVersion?: string;
  priority?: number;
  lastConnectedAt?: number;
}

interface SerializedAccount {
  address: string;
  publicKey: number[];
  walletName: string;
  chains: string[];
  label?: string;
}

/**
 * Creates a persistence manager for a specific config instance.
 * This encapsulates the debounce timeout per-config to avoid shared state issues.
 */
export interface PersistenceManager {
  persist(state: FrostAppState): void;
  clear(): void;
  destroy(): void;
}

export function createPersistenceManager(key: string, storage: Storage): PersistenceManager {
  let persistTimeout: ReturnType<typeof setTimeout> | null = null;

  return {
    persist(state: FrostAppState): void {
      if (persistTimeout) {
        clearTimeout(persistTimeout);
      }

      persistTimeout = setTimeout(() => {
        try {
          const serialized: SerializedState = {
            walletName: state.walletName,
            accountAddress: state.accountAddress,
            connectedAt: state.connectedAt,
            sessionExpiresAt: state.sessionExpiresAt,
            lastUsedAt: state.lastUsedAt,
            userDisconnected: state.userDisconnected,
            wallets: Array.from(state.wallets.entries()).map(([k, v]) => [
              k,
              {
                name: v.name,
                // icon intentionally excluded
                chains: [...v.chains],
                features: [...v.features],
                installedVersion: v.installedVersion,
                priority: v.priority,
                lastConnectedAt: v.lastConnectedAt,
              },
            ]),
            accounts: Array.from(state.accounts.entries()).map(([k, v]) => [
              k,
              {
                address: v.address,
                publicKey: Array.from(v.publicKey),
                walletName: v.walletName,
                chains: [...v.chains],
                label: v.label,
              },
            ]),
          };

          const schema: StorageSchema = {
            version: STORAGE_VERSION,
            state: serialized,
          };

          storage.setItem(key, JSON.stringify(schema));
        } catch (error) {
          // Storage might be full or unavailable
          console.error("[Frost] Failed to persist state:", error);
        }
      }, 250); // 250ms debounce
    },

    clear(): void {
      storage.removeItem(key);
    },

    destroy(): void {
      if (persistTimeout) {
        clearTimeout(persistTimeout);
        persistTimeout = null;
      }
    },
  };
}

/**
 * Load persisted state from storage.
 *
 * Important behaviors:
 * - Always returns status as "disconnected" - reconnect() handles re-establishing connection
 * - Validates session expiry - returns null if session has expired
 * - chainId is not loaded - must be provided separately from client
 */
export function loadPersistedState(
  key: string,
  storage: Storage,
  chainId: string
): Omit<FrostAppState, "_walletRefs"> | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;

    const parsed: StorageSchema = JSON.parse(raw);

    // Version check - clear storage on version mismatch
    if (parsed.version !== STORAGE_VERSION) {
      storage.removeItem(key);
      return null;
    }

    const { state } = parsed;

    // Validate session expiry - if expired, don't restore connection state
    const now = Date.now();
    const sessionExpired = state.sessionExpiresAt && state.sessionExpiresAt < now;

    if (sessionExpired) {
      // Session expired - return state but clear connection-related fields
      return {
        status: "disconnected",
        chainId,
        walletName: null,
        accountAddress: null,
        connectedAt: null,
        sessionExpiresAt: null,
        lastUsedAt: state.lastUsedAt,
        userDisconnected: false,
        wallets: new Map(
          state.wallets.map(([k, v]) => [
            k,
            {
              ...v,
              // icon will be populated when wallet is rediscovered
              chains: (v.chains ?? []) as IdentifierString[],
              features: v.features ?? [],
            },
          ])
        ),
        accounts: new Map(), // Clear accounts on session expiry
      };
    }

    return {
      // Always load as disconnected - reconnect() will re-establish if appropriate
      status: "disconnected",
      // chainId comes from client, not persistence
      chainId,
      walletName: state.walletName,
      accountAddress: state.accountAddress,
      connectedAt: state.connectedAt,
      sessionExpiresAt: state.sessionExpiresAt,
      lastUsedAt: state.lastUsedAt,
      userDisconnected: state.userDisconnected ?? false,
      wallets: new Map(
        state.wallets.map(([k, v]) => [
          k,
          {
            ...v,
            // icon will be populated when wallet is rediscovered
            chains: (v.chains ?? []) as IdentifierString[],
            features: v.features ?? [],
          },
        ])
      ),
      accounts: new Map(
        state.accounts.map(([k, v]) => [
          k,
          {
            ...v,
            publicKey: new Uint8Array(v.publicKey),
            chains: (v.chains ?? []) as IdentifierString[],
            // Note: _raw is not persisted - will be populated on reconnect
          },
        ])
      ),
    };
  } catch {
    return null;
  }
}
